export type AvatarColor = {
  background: string;
  foreground: string;
};

/** Stable palette order — do not reorder. */
export const AVATAR_COLORS: readonly AvatarColor[] = [
  { background: "#E8F0FE", foreground: "#365276" }, // blue
  { background: "#E4F3F1", foreground: "#2E625D" }, // teal
  { background: "#EAF3E5", foreground: "#45633C" }, // green
  { background: "#F6EEDB", foreground: "#6D5525" }, // gold
  { background: "#EEE9F7", foreground: "#5C477A" }, // lavender
  { background: "#F6E8EC", foreground: "#764556" }, // rose
];

const CONNECTOR_WORDS = new Set(["a", "an", "and", "at", "by", "for", "in", "of", "on", "the", "o"]);

const LEGAL_SUFFIXES = new Set(["co", "company", "corp", "corporation", "inc", "llc", "ltd"]);

/** Trim, lowercase, and collapse repeated whitespace. */
export function normalizeBusinessName(name: string | null | undefined): string {
  return (name ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Explicit djb2 32-bit hash. Stable across processes/sessions/deployments.
 * Color index: Math.abs(hash) % AVATAR_COLORS.length
 */
export function hashString(value: string): number {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
  }
  return hash;
}

export function getAvatarColor(name: string | null | undefined): AvatarColor {
  const normalized = normalizeBusinessName(name);
  const index = Math.abs(hashString(normalized)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

/** Strip surrounding punctuation; keep letters and meaningful digits. */
function stripWordPunctuation(word: string): string {
  return word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "");
}

function isAlphanumericChar(char: string): boolean {
  return /\p{L}|\p{N}/u.test(char);
}

/**
 * Two uppercase initials from the first two meaningful words, or the first two
 * alphanumeric chars of a single meaningful word. Returns "" when unusable.
 */
export function getBusinessInitials(name: string | null | undefined): string {
  const trimmed = (name ?? "").trim();
  if (trimmed.length < 2) return "";

  const words = trimmed.split(/\s+/).map(stripWordPunctuation).filter(Boolean);

  const meaningful = words.filter(word => {
    const lower = word.toLowerCase();
    return !CONNECTOR_WORDS.has(lower) && !LEGAL_SUFFIXES.has(lower);
  });

  if (meaningful.length >= 2) {
    const first = [...meaningful[0]].find(isAlphanumericChar);
    const second = [...meaningful[1]].find(isAlphanumericChar);
    if (!first || !second) return "";
    return (first + second).toUpperCase();
  }

  if (meaningful.length === 1) {
    const chars = [...meaningful[0]].filter(isAlphanumericChar);
    if (chars.length < 2) return "";
    return (chars[0] + chars[1]).toUpperCase();
  }

  return "";
}
